import { Select, Skeleton } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import useSWR from 'swr';
import { getFetcher } from '@/api/fetchers';
import { ServiceContext } from '@/layouts/Person/PersonLayout';
import { type Service } from '@/schemas/API/GET/service/response';
import { okNotification } from '../Notification/Notification';

interface Props {
  checkForServicePresence: boolean;
  presentServicesId: number[];
}

export const ServiceSelector = ({
  checkForServicePresence,
  presentServicesId,
}: Props): JSX.Element => {
  const { service, setService } = useContext(ServiceContext);

  const { data: services } = useSWR<Service[]>(`/services`, getFetcher);

  // const getNameByCode = (code: string): string | undefined => {
  //   if (!services) return undefined;
  //   return services.find(s => s.code === code)?.name;
  // };

  const getCodeByName = (name: string): string | undefined => {
    if (!services) return undefined;
    return services.find(s => s.name === name)?.code;
  };

  const navigate = useNavigate();
  const location = useLocation();

  const handleServiceChange = (servCode: string): void => {
    if (!services) return;
    if (!services.some(s => s.code === servCode)) {
      console.error(
        `Error changing service - service with code ${servCode} does not exist`
      );
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    searchParams.set('service', servCode);

    navigate({
      pathname: location.pathname,
      search: `?${searchParams.toString()}`,
    });

    setService(services.find(s => s.code === servCode) as Service);
  };

  // at the start - set the service from the URL
  useEffect(() => {
    if (!services) return;

    const searchParams = new URLSearchParams(location.search);
    const servCode: string | null = searchParams.get('service');

    if (servCode && services.some(s => s.code === servCode)) {
      setService(services.find(s => s.code === servCode) as Service);
      handleServiceChange(servCode);
      return;
    }

    if (!checkForServicePresence) {
      setService(services[0]);
      handleServiceChange(services[0].code);
      return;
    }

    if (presentServicesId.length === 0) {
      return;
    }

    const presentService = services.find(s => s.id === presentServicesId[0]);

    if (presentService === undefined) {
      return;
    }

    setService(presentService);
    handleServiceChange(presentService.code);
  }, [services]);

  if (services && service) {
    return (
      <Select
        radius="md"
        placeholder="Vyberte službu"
        value={service.name}
        onChange={servName => {
          if (!servName) return;

          const servCode = getCodeByName(servName);
          if (!servCode) {
            console.error(
              `Error changing service, name for ${servCode} is undefined`
            );
            return;
          }

          handleServiceChange(servCode);
        }}
        data={services.map(s => {
          if (!checkForServicePresence) return { label: s.name, value: s.name };

          const isPresent = presentServicesId.find(
            presentServiceId => s.id === presentServiceId
          );

          return {
            label: s.name,
            value: s.name,
            disabled: isPresent === undefined,
          };
        })}
        allowDeselect={false}
      />
    );
  } else {
    return <Skeleton height={30} width="20%" radius="md" />;
  }
};
